import { Platform, ViewStyle } from 'react-native';
import { MD3DarkTheme, MD3LightTheme, MD3Theme } from 'react-native-paper';
import { DarkTheme as NavDarkTheme, DefaultTheme as NavLightTheme, Theme as NavTheme } from '@react-navigation/native';

/**
 * Design tokens — Airbnb-inspired: airy white canvas, ink-black typography,
 * a single coral accent for primary actions, generous radii and soft shadows.
 * Every color used by screens must come from a Palette so the dark mode works.
 */

export type ThemeMode = 'light' | 'dark';
export type ThemePreference = ThemeMode | 'system';

export type Palette = {
  mode: ThemeMode;
  /** Coral accent: primary buttons, active tab, links. */
  primary: string;
  primaryPressed: string;
  primarySoft: string;
  onPrimary: string;
  /** GeoFleete brand blue (logo). Secondary accent / informational highlights. */
  brand: string;
  brandSoft: string;
  onBrand: string;
  /** Ink: Airbnb-style near-black used for selected chips and secondary CTAs. */
  ink: string;
  onInk: string;

  background: string;
  surface: string;
  surfaceAlt: string;
  surfaceRaised: string;
  overlay: string;

  textPrimary: string;
  textSecondary: string;
  textMuted: string;

  border: string;
  divider: string;

  success: string;
  successSoft: string;
  warning: string;
  warningSoft: string;
  danger: string;
  dangerSoft: string;
  info: string;
  infoSoft: string;

  /** Order lifecycle colors. */
  devis: string;
  valide: string;
  facture: string;

  shadow: string;
};

export const LIGHT_COLORS: Palette = {
  mode: 'light',
  primary: '#E61E4D',
  primaryPressed: '#C8133F',
  primarySoft: '#FFF0F3',
  onPrimary: '#FFFFFF',
  brand: '#314C9B',
  brandSoft: '#EEF2FB',
  onBrand: '#FFFFFF',
  ink: '#222222',
  onInk: '#FFFFFF',

  background: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceAlt: '#F7F7F7',
  surfaceRaised: '#FFFFFF',
  overlay: 'rgba(0, 0, 0, 0.45)',

  textPrimary: '#222222',
  textSecondary: '#6A6A6A',
  textMuted: '#8A8A8A',

  border: '#DDDDDD',
  divider: '#EBEBEB',

  success: '#008A05',
  successSoft: '#E9F7EA',
  warning: '#B35A00',
  warningSoft: '#FFF4E5',
  danger: '#C13515',
  dangerSoft: '#FFF1EE',
  info: '#1D5FD1',
  infoSoft: '#EEF4FF',

  devis: '#B35A00',
  valide: '#314C9B',
  facture: '#008A05',

  shadow: '#000000',
};

export const DARK_COLORS: Palette = {
  mode: 'dark',
  primary: '#E8325A',
  primaryPressed: '#FF4F70',
  primarySoft: '#3A1520',
  onPrimary: '#FFFFFF',
  brand: '#8FA6EE',
  brandSoft: '#1D2540',
  onBrand: '#0E1530',
  ink: '#F5F5F5',
  onInk: '#121212',

  background: '#121212',
  surface: '#1C1C1E',
  surfaceAlt: '#242426',
  surfaceRaised: '#2A2A2D',
  overlay: 'rgba(0, 0, 0, 0.65)',

  textPrimary: '#F5F5F5',
  textSecondary: '#B4B4B4',
  textMuted: '#8C8C8C',

  border: '#3A3A3C',
  divider: '#2C2C2E',

  success: '#4CD47A',
  successSoft: '#12301D',
  warning: '#FFB547',
  warningSoft: '#352711',
  danger: '#FF7A66',
  dangerSoft: '#3A1814',
  info: '#79AAFF',
  infoSoft: '#152443',

  devis: '#FFB547',
  valide: '#8FA6EE',
  facture: '#4CD47A',

  shadow: '#000000',
};

export const SPACING = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
  xxxl: 48,
} as const;

export const RADIUS = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  pill: 999,
} as const;

/** Breakpoints shared by every responsive layout. */
export const BREAKPOINTS = {
  /** Below: phone layout (bottom tab bar). At or above: desktop/tablet layout (top navigation). */
  desktop: 768,
  wide: 1128,
} as const;

/** Max width of the centered content column on large screens (Airbnb uses ~1120px). */
export const CONTENT_MAX_WIDTH = 1200;

export const TYPOGRAPHY = {
  display: { fontSize: 30, lineHeight: 36, fontWeight: '800' as const, letterSpacing: -0.4 },
  title: { fontSize: 22, lineHeight: 28, fontWeight: '700' as const, letterSpacing: -0.2 },
  heading: { fontSize: 18, lineHeight: 24, fontWeight: '700' as const },
  subheading: { fontSize: 16, lineHeight: 22, fontWeight: '600' as const },
  body: { fontSize: 15, lineHeight: 22, fontWeight: '400' as const },
  bodyStrong: { fontSize: 15, lineHeight: 22, fontWeight: '600' as const },
  label: { fontSize: 13, lineHeight: 18, fontWeight: '600' as const },
  caption: { fontSize: 12, lineHeight: 16, fontWeight: '500' as const },
  overline: { fontSize: 11, lineHeight: 14, fontWeight: '700' as const, letterSpacing: 0.6 },
};

/**
 * Soft, layered shadows in light mode; in dark mode depth comes from lighter
 * surfaces + borders (shadows are invisible on dark backgrounds).
 */
export function shadow(colors: Palette, level: 1 | 2 | 3 = 1): ViewStyle {
  if (colors.mode === 'dark') return {};
  const presets = {
    1: { y: 2, radius: 8, opacity: 0.06, elevation: 1 },
    2: { y: 6, radius: 16, opacity: 0.1, elevation: 3 },
    3: { y: 10, radius: 28, opacity: 0.16, elevation: 8 },
  }[level];
  return Platform.select<ViewStyle>({
    android: { elevation: presets.elevation },
    default: {
      shadowColor: colors.shadow,
      shadowOffset: { width: 0, height: presets.y },
      shadowOpacity: presets.opacity,
      shadowRadius: presets.radius,
    },
  });
}

export function paletteFor(mode: ThemeMode): Palette {
  return mode === 'dark' ? DARK_COLORS : LIGHT_COLORS;
}

export function buildPaperTheme(colors: Palette): MD3Theme {
  const base = colors.mode === 'dark' ? MD3DarkTheme : MD3LightTheme;
  return {
    ...base,
    roundness: 4,
    colors: {
      ...base.colors,
      primary: colors.primary,
      onPrimary: colors.onPrimary,
      primaryContainer: colors.primarySoft,
      onPrimaryContainer: colors.mode === 'dark' ? '#FFD9E0' : '#5C0A1E',
      secondary: colors.ink,
      onSecondary: colors.onInk,
      secondaryContainer: colors.surfaceAlt,
      onSecondaryContainer: colors.textPrimary,
      tertiary: colors.brand,
      onTertiary: colors.onBrand,
      tertiaryContainer: colors.brandSoft,
      onTertiaryContainer: colors.textPrimary,
      background: colors.background,
      onBackground: colors.textPrimary,
      surface: colors.surface,
      onSurface: colors.textPrimary,
      surfaceVariant: colors.surfaceAlt,
      onSurfaceVariant: colors.textSecondary,
      outline: colors.border,
      outlineVariant: colors.divider,
      error: colors.danger,
      onError: '#FFFFFF',
      errorContainer: colors.dangerSoft,
      onErrorContainer: colors.danger,
      inverseSurface: colors.ink,
      inverseOnSurface: colors.onInk,
      inversePrimary: colors.mode === 'dark' ? LIGHT_COLORS.primary : DARK_COLORS.primary,
      backdrop: colors.overlay,
      elevation: {
        level0: 'transparent',
        level1: colors.surface,
        level2: colors.surfaceRaised,
        level3: colors.surfaceRaised,
        level4: colors.surfaceRaised,
        level5: colors.surfaceRaised,
      },
    },
  };
}

export function buildNavigationTheme(colors: Palette): NavTheme {
  const base = colors.mode === 'dark' ? NavDarkTheme : NavLightTheme;
  return {
    ...base,
    dark: colors.mode === 'dark',
    colors: {
      ...base.colors,
      primary: colors.primary,
      background: colors.background,
      card: colors.background,
      text: colors.textPrimary,
      border: colors.divider,
      notification: colors.primary,
    },
  };
}
